// Activity events emitted by `content/annotations`.
//
// Same conventions as `articles/events.ts`: dot-segment kind strings,
// `ANNOTATION_ACTIVITY_KINDS` is the source-of-truth constant, every
// emit site references it.
//
// Payload mask discipline: the activity row is durable + fans out to
// many subscribers (notifications, future digest, future analytics).
// We inline only ids + audit fields. The annotation's `content` is
// NOT inlined — it's user-supplied free-form text and may carry PII;
// subscribers re-resolve through the Annotation linkable for current
// shape.
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Kind constants.
// ---------------------------------------------------------------------------
export const ANNOTATION_ACTIVITY_KINDS = {
    Created: 'annotation.created',
    Updated: 'annotation.updated',
    Resolved: 'annotation.resolved',
    Unresolved: 'annotation.unresolved',
    Archived: 'annotation.archived',
    /** Server-side reanchor pass found the captured `anchorTextSnippet`
     *  at a new (blockId, offset) in the freshly-edited article body and
     *  rewrote the row in place. Subscribers may invalidate cached
     *  annotation feeds keyed by `(articleId, anchorBlockId)`. */
    Repositioned: 'annotation.repositioned',
    /** Server-side reanchor pass could NOT locate the snippet in the new
     *  body. The annotation is left at its old offsets; the editor
     *  detects the mismatch on render and surfaces an "anchor lost"
     *  warning. Subscribed by the orphan-notification subject (gated). */
    Orphaned: 'annotation.orphaned',
};
/** Glob pattern matching every annotation-emitted kind. Used by
 *  subscribers that want a coarse "tell me about anything annotation-
 *  shaped" subscription (notifications dispatcher, future digest). */
export const ANNOTATION_ACTIVITY_KIND_GLOB = 'annotation.*';
// ---------------------------------------------------------------------------
// Per-kind payload schemas. Wrapped by ActivityEvent at the transport.
// ---------------------------------------------------------------------------
export const AnnotationCreatedEvent = z.object({
    type: z.literal('annotation.created'),
    annotationId: z.string().uuid(),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    /** 'comment' | 'insertion' | 'deletion' — lets subscribers route by
     *  variant without a refetch. */
    annotationType: z.enum(['comment', 'insertion', 'deletion']),
    occurredAt: z.string().datetime({ offset: true }),
});
export const AnnotationUpdatedEvent = z.object({
    type: z.literal('annotation.updated'),
    annotationId: z.string().uuid(),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    byUserId: z.string().uuid(),
    /** Closed list of fields the request mutated. */
    changedFields: z.array(z.string()),
    occurredAt: z.string().datetime({ offset: true }),
});
export const AnnotationResolvedEvent = z.object({
    type: z.literal('annotation.resolved'),
    annotationId: z.string().uuid(),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    /** User who marked it resolved (annotation author / article author /
     *  manager+). */
    resolvedBy: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const AnnotationUnresolvedEvent = z.object({
    type: z.literal('annotation.unresolved'),
    annotationId: z.string().uuid(),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    byUserId: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const AnnotationArchivedEvent = z.object({
    type: z.literal('annotation.archived'),
    annotationId: z.string().uuid(),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    byUserId: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const AnnotationRepositionedEvent = z.object({
    type: z.literal('annotation.repositioned'),
    annotationId: z.string().uuid(),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    /** Anchor location BEFORE the rewrite. */
    fromBlockId: z.string(),
    fromOffset: z.number().int().nonnegative(),
    /** Anchor location AFTER the rewrite. `toBlockId` may equal
     *  `fromBlockId` when only the offset shifted. */
    toBlockId: z.string(),
    toOffset: z.number().int().nonnegative(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const AnnotationOrphanedEvent = z.object({
    type: z.literal('annotation.orphaned'),
    annotationId: z.string().uuid(),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    /** Anchor location at the time the snippet was captured. Subscribers
     *  rendering an "anchor lost" surface use this to display the old
     *  pointer for context. */
    lastBlockId: z.string(),
    lastOffset: z.number().int().nonnegative(),
    occurredAt: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=events.js.map